import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import { QuestionOperations, QuestionSetOperations } from '../../data/Firestore';
import { isNonEmptyString } from '../../util/Text';

/** Simple set of Chips representing Question / Question Set tags */
export const Tags: FunctionComponent<{ tags: string[]; }> = (props) =>
	<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', margin: '2px' }}>
		{props.tags.map(tag =>
			<Chip key={tag} label={tag} size="small" />
		)}
	</Box>;

type EntryProps = {
	tags: string[],
	setTags: Dispatch<SetStateAction<string[]>>,
};

type TagProvider = {
	getAll: () => Promise<{ tags: string[]; }[]>;
};

export const QuestionTagEntry: FunctionComponent<EntryProps> = (props) =>
	<TagEntry provider={QuestionOperations} {...props} />;

export const QuestionSetTagEntry: FunctionComponent<EntryProps> = (props) =>
	<TagEntry provider={QuestionSetOperations} {...props} />;

const TagEntry: FunctionComponent<EntryProps & { provider: TagProvider; }> = (props) => {
	const allTags = useAllTags(props.provider);

	return <Autocomplete multiple freeSolo
		value={props.tags}
		options={allTags}
		onChange={(_, newValue) => {
			const normalized = newValue
				.map(normalizeTag)
				.filter(isNonEmptyString)
				.uniqueValues()
				.sort();

			props.setTags(normalized);
		}}
		filterOptions={(options, state) => {
			const inputValue = normalizeTag(state.inputValue);
			return options.filter(option => option.includes(inputValue));
		}}
		renderInput={(params) => <TextField {...params} label="Tags" />}
	/>;
};

export function useAllTags(provider: TagProvider) {
	const [tags, setTags] = useState<string[]>([]);

	async function load() {
		const parents = await provider.getAll();
		const allTags = parents
			.flatMap(q => q.tags)
			.map(normalizeTag)
			.uniqueValues()
			.sort();

		setTags(allTags);
	}

	useEffect(() => { void load(); }, []);

	return tags;
}

function normalizeTag(input: string): string {
	return input.toLowerCase()
		.trim()
		.replace(/\s+/g, '-');
}